/**
 * WHICH FACE ASKS WHAT, in one place, so Feedback and an invoice's "How did we do?" ask the same of
 * the same face (slice V4, `docs/seam.md` P51). Two rules, and they are not one:
 *
 * - WHAT WAS MISSED, home's to ask. Every face below Very good asks which area was missed and what,
 *   both hers to leave, and shows the business's promise to clean it again. The owner decided on
 *   2026-10-16 (ops #72) that any answer naming a missed area starts a re-clean, Good included, so
 *   Good asks it on both pages; the replay of the owner's walk found the invoice sending Good on the
 *   tap, so it could never name one. Very good asks nothing more and goes at once.
 * - BELOW GOOD, ops's to count. A face below `happy` opens the office's follow-up and holds the
 *   saved method's charge (ops's `docs/seams/home.md` section 4, l.563-564 at d1c7636), and the
 *   invoice page thanks a good face plainly and folds the ways to pay only after a sad one (section
 *   5, l.759-767). Home draws the thanks, the folding and the saved method's "Nothing is charged"
 *   by it, and does not widen it: Good folds nothing and is never told the office will put it right.
 *
 * And what the asking sends and promises, one rule for both pages (round four, slice F1):
 *
 * - THE AREA NAMED. Ops opens the re-clean for any answer that names an area, and "An area is named
 *   when `area` has words once trimmed: a `line` alone names none" (ops's `docs/seams/home.md`,
 *   section 4 at 9f10a8b). So a named chip sends itself, and her line, under the Other chip or with
 *   no chip, sends "Other" with it, as ops reads it (section 11.6 at 430c053, question 7): the replay
 *   of the owner's walk found Good with Other, or with only a line, starting nothing. The Other chip
 *   with nothing written names none, as the owner accepted on 2026-09-28: question 7 reads "Other"
 *   with a written line and no more, and ops would open a missed-area issue that says nothing.
 * - THE CHARGE AFTER GOOD. Ops holds the saved method's charge after "a good face that names an
 *   area ... until the office has put it right and released the hold" (section 4 at 9f10a8b), and
 *   pushes her face and never whether it named one. Good is the face that may, so after Good home
 *   says nothing of when the charge comes, the plain thanks on the invoice and no line on its card,
 *   until ops's section 5 (ops #72) says what they say then (`chargeFollows`).
 * - THE PROMISE WHILE IT HOLDS. "Tell us within 24 hours, on your client page, by phone or by email, and we clean it again at no charge." is
 *   drawn on the clean's own day and the day after, in Florida's days, and not after.
 */
import { AREAS, OTHER_AREA, type Face } from '../words.ts';
import { daysSince } from './dates.ts';

/** Whether the face asks which area was missed and what, with the re-clean promise. */
export const asksWhatWasMissed = (face: Face): boolean => face !== 'delighted';

/** Whether the face is below Good, as ops counts it; no face yet is not. */
export const belowGood = (face: Face | null): boolean => face === 'unhappy' || face === 'so_so';

/**
 * Whether home says the saved method's charge goes ahead after the face that counts: after Very good
 * alone, which asks nothing more and names no area. Below Good holds it, and so may Good, which may
 * name an area, and ops's push of the invoice carries her face and never her area.
 */
export const chargeFollows = (face: Face): boolean => !asksWhatWasMissed(face);

/**
 * The `area` an answer that asks what was missed sends: a named chip's own (`AREAS`); else
 * `OTHER_AREA` where she wrote a line with words in it, under the Other chip or with none, since a
 * line alone names no area; else none, the Other chip with nothing written included. `chip` is the
 * label of the chip chosen, or null for none.
 */
export function areaSent(chip: string | null, line: string): string | null {
  const named = AREAS.find(({ label }) => label === chip)?.area;
  if (named !== undefined && named !== null) return named;
  return line.trim() === '' ? null : OTHER_AREA;
}

/**
 * Whether the re-clean promise is drawn: on the clean's own day and the day after, `visitDay` and
 * `today` both the business's days from the place. Not for a clean with no day or one still to
 * come, nor for a day that is not real.
 */
export function recleanPromised(visitDay: string | undefined, today: string): boolean {
  if (visitDay === undefined) return false;
  const since = daysSince(visitDay, today);
  return since === 0 || since === 1;
}
